import { COMMANDS } from '../../utils/commonProperties';
import * as THREE from 'three';
import { useEffect, useRef   } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setCommandMessage, resetOperation, setCommandType, setOperationData } from '../../redux/operationSlice';
import { clearSelection } from '../../redux/selectionSlice';
import { useComponentWiring } from '../../modules/wiring';
import parseColor from '../../utils/parseColor';
import { createBell, createBellTrafo } from '../../factory/symbols/createBellSymbols';
import { generateUniqueId } from '../../utils/generateUniqueId';
import useSnapPoints from '../../shared/snap/useSnapPoints';
import { FeederFactory } from '../../factories/FeederFactory';

// 🆕 Undo/Redo imports
import { BellCommand } from '../../modules/undoRedo';
import { findObjectByUUID } from '../../utils/findObjectByUUID';

const useBell = (scene, camera, renderer, undoRedoManagerRef) => {
    const dispatch = useDispatch();
    const { commandType, data: operationData } = useSelector((state) => state.operation);
    const { snapPoints } = useSnapPoints(scene);



    const dynamicSnapPoints = useRef([]);
    const bellTrafoRef = useRef(null); // ✅ Bell trafo referansı
    const wiringActiveRef = useRef(false);
    const sceneRef = useRef(null);
    const settings = useSelector(state => state.settings);
    const parsedWire = parseColor(settings?.lowCurrent?.wireColor ?? 0x0045ff);
    const parsedPreviewWire = parseColor(settings?.lowCurrent?.wirePreviewColor ?? parsedWire.colorNumber);
    const wireColor = parsedWire.colorNumber;

    // 🆕 Bell Configuration
    const bellConfig = {
        commandType: COMMANDS.DRAW_BELL,
        itemTypeName: 'bell',
        wireColor: parsedWire.colorNumber,
        allowSpaceKey: false, // ✅ Bell'de Space ile ekleme yok, sadece Enter
        skipItemExecution: true, // ✅ addItem()'da execute atlanır, handleWireEnd'de yapılır
        markerCount: 0, // ✅ Zil hatlarında wireMarker oluşturma
        createSymbol: (position, direction, angle, isPreview, itemType, extraData = {}) => {
            const colorSetting = isPreview
                ? (settings?.lowCurrent?.wirePreviewColor ?? parsedWire.colorNumber)
                : parsedWire.colorNumber;
            const parsed = parseColor(colorSetting);

            return createBell(
                null,
                position,
                parsed.colorNumber,
                isPreview,
                itemType?.type || 'Zil',
                angle
            );
        },
        // ❌ createCommand KALDIRILDI - Bell için command sadece handleWireEnd içinde oluşturulur
        createCommand: null,
        itemTypes: [
            { name: 'Zil', type: 'Zil', power: 5 }
        ],
        onKeyDown: null
    };

    // ✅ Bell trafo yerleştirme (ilk nokta tıklandığında)
    const handleFirstPoint = (point, snapSource, verticesRef) => {
 
        // Direction hesapla (panel çıkışına dik)
        let direction = new THREE.Vector3(1, 0, 0);
        const panel = snapSource?.userData?.sourceObject;

        if (panel) {
            const panelWorldQuat = new THREE.Quaternion();
            panel.getWorldQuaternion(panelWorldQuat);
            const panelWorldEuler = new THREE.Euler().setFromQuaternion(panelWorldQuat);
            const panelRotation = panelWorldEuler.z || 0;
            const perpendicularAngle = panelRotation + Math.PI / 2;
            direction = new THREE.Vector3(
                Math.cos(perpendicularAngle),
                Math.sin(perpendicularAngle),
                0
            );
        }

        // Bell trafosunu yerleştir
        const bellTrafo = createBellTrafo(scene, point, wireColor, false, direction);
        const trafoId = generateUniqueId("bellTrafo");
        bellTrafo.userData = {
            ...bellTrafo.userData,
            id: trafoId,
            type: 'BellTrafo',
            isElectrical: true
        };

        // ✅ sceneRef.current veya doğrudan scene kullan
        const targetScene = sceneRef.current || scene;
        if (targetScene) {
            targetScene.add(bellTrafo);
        } else {
            console.warn('⚠️ [useBell] handleFirstPoint: Scene bulunamadı!');
        }
        bellTrafoRef.current = bellTrafo;

        // ✅ Trafo çıkış noktasını hesapla ve ilk vertex'i güncelle
        if (bellTrafo.userData.outputMarker && verticesRef) {
            const trafoOutputPoint = new THREE.Vector3();
            bellTrafo.userData.outputMarker.getWorldPosition(trafoOutputPoint);

 
            // İlk vertex'i trafo çıkışına güncelle (wire trafo çıkışından başlasın)
            if (verticesRef.current && verticesRef.current.length > 0) {
                verticesRef.current[0] = trafoOutputPoint.clone();
            }
        }
    };

    useEffect(() => {
        if (commandType !== COMMANDS.DRAW_BELL || !scene || !camera || !renderer) return;

        dispatch(setCommandMessage('Zil tesisatı: Hatta tıklayın'));

        sceneRef.current = scene;

        // Sadece panel çıkışlarını snap noktası olarak al
        dynamicSnapPoints.current = [];
        scene.traverse(obj => {
            if (obj.userData?.connectionPoints) {
                obj.userData.connectionPoints.forEach(p => {
                    if (p.type === 'panelCon_out') {
                        const worldPos = p.position.clone().applyMatrix4(obj.matrixWorld);
                        dynamicSnapPoints.current.push({
                            no: obj.uuid,
                            position: worldPos,
                            type: 'panelConnection'
                        });
                    }
                });
            }
        });

        wiringActiveRef.current = true;

        // ✅ Cleanup: commandType değiştiğinde wiring'i devre dışı bırak
        return () => {
            wiringActiveRef.current = false;
        };
    }, [commandType, scene, camera, renderer]);

    // ✅ Bell placement ve removal handlers
    const handleBellPlace = (position, direction) => {
         return null;
    };

    const handleBellRemove = (bell) => {
        if (!bell) return;
        sceneRef.current.remove(bell);
        bell.traverse(child => {
            if (child.geometry) child.geometry.dispose();
            if (child.material) child.material.dispose();
        });
    };

    // Wire end handler
    const handleWireEnd = (vertices, bells, source, bell, tempLineUUID, wireMarkers) => {
        if (!source || !source.p) {
            return;
        }

        // Bell data (bells array veya direkt bell parametresi)
        const bellData = bell || (bells && bells.length > 0 ? bells[0] : null);
        if (!bellData) {
            console.warn('No bell data found');
            return;
        }

        let panelID = source.p.panelID;
        const power = bellData.power || 5;
        const bellType = bellData.type || 'Zil';
        let totalLength = 0;
                for (let i = 0; i < vertices.length - 1; i++) {
                    totalLength += vertices[i].distanceTo(vertices[i + 1]);
                }

                const foundPanelOutSnap = (source.type === "panelConnection")
                    ? findObjectByUUID(scene, source.p.UUID)
                    : findObjectByUUID(scene, source.no);

                const panel = source?.userData?.sourceObject ?? foundPanelOutSnap;

                // ✅ Branch ve Wire ID üret
                const branchID = generateUniqueId("branchBell");
                const wireID = generateUniqueId("wireBell");
                // ✅ Bell trafo - handleFirstPoint'te oluşturuldu
                const bellTrafo = bellTrafoRef.current;
                const trafoId = (bellTrafo && bellTrafo.userData && bellTrafo.userData.id) ? bellTrafo.userData.id : generateUniqueId("bellTrafo");
                const bellId = generateUniqueId("bell");

                // 🆕 feederId'yi önce tanımla
                const bellFeederIdx = generateUniqueId("feederBell");

                // 🆕 YENİ SİSTEM: addLoadToComponent kullan
                const newBellLoadData = {
                    id: generateUniqueId('load'),
                    name: bellType,
                    branchType: 'bell',
                    power: power,
                    phases: 1,
                    electrical: {
                        installedPower: power,
                        current: power / 12, // 12V zil trafosu
                        voltage: 12,
                        phaseCount: 1,
                    },
                    parentComponentId: panel.userData?.id || panel.id,
                    metadata: {
                        wireId: wireID,
                        branchId: branchID,
                        length: totalLength.toFixed(2),
                        bellType: bellType,
                        bell: bell,
                        loadType: 'bell'
                    }
                };

                // FeederFactory ile doğru cable ve electrical bilgilerle bell feeder oluştur
                const bellFeeder = FeederFactory.createFeederForLoad(
                    'bell',
                    power,
                    panel.userData?.id || panel.id,
                    newBellLoadData.id,
                    {
                        id: bellFeederIdx,
                        vertices: vertices.map(v => ({ x: v.x, y: v.y, z: v.z })),
                        cable: {
                            indexNo: 1, // ✅ CABLES[1] = Bell cable
                            type: 'Bell Cable 2x0.5',
                            crossSection: 0.5,
                            length: Math.round(totalLength / 10)
                        },
                        electrical: {
                            voltage: 12,
                            current: power / 12
                        },
                        metadata: {
                            branchType: 'bell',
                            length: totalLength.toFixed(2),
                            bellType: bellType,
                            wireId: wireID
                        }
                    }
                );

                const bellFeederData = bellFeeder;

                // ✅ Bell açısını hesapla (wire yönüne göre)
                const lastVertex = vertices[vertices.length - 1];
                const prevVertex = vertices[vertices.length - 2] || vertices[vertices.length - 1];
                const direction = new THREE.Vector3().subVectors(lastVertex, prevVertex);
                const bellAngle = Math.atan2(direction.y, direction.x) - Math.PI / 2;

                // ✅ Bell trafo position (ilk vertex)
                const trafoPosition = vertices[0];

                // ✅ Bell trafo direction
                const trafoDirection = source.userData?.direction || new THREE.Vector3(1, 0, 0);

                // ✅ Wire markers'ı serialize et
                const wireMarkersData = wireMarkers.map(marker => {
                    if (marker.geometry?.attributes?.position) {
                        const positions = marker.geometry.attributes.position.array;
                        return {
                            start: { x: positions[0], y: positions[1], z: positions[2] },
                            end: { x: positions[3], y: positions[4], z: positions[5] }
                        };
                    }
                    return null;
                }).filter(m => m !== null);

                // Ensure bellTrafo (created during wiring start) has branchID and correct color
                if (bellTrafo) {
                    try {
                        bellTrafo.userData = {
                            ...bellTrafo.userData,
                            id: trafoId,
                            branchID: branchID,
                            type: 'BellTrafo',
                            isElectrical: true
                        };
                        // Update material color for all children that have a material
                        bellTrafo.traverse(child => {
                            if (child.material) {
                                if (Array.isArray(child.material)) {
                                    child.material.forEach(m => {
                                        if (m.color && typeof m.color.setHex === 'function') m.color.setHex(wireColor);
                                        m.opacity = 1;
                                        m.transparent = false;
                                    });
                                } else {
                                    const m = child.material;
                                    if (m.color && typeof m.color.setHex === 'function') m.color.setHex(wireColor);
                                    m.opacity = 1;
                                    m.transparent = false;
                                }
                            }
                        });
                    } catch (e) {
                        console.warn('Failed to patch bellTrafo userData/color', e);
                    }
                }

                // ✅ restoreSource: UNDO/REDO için kaynak bilgisi
                const restoreSource = {
                    type: source?.type || 'panelConnection',
                    p: source?.p,
                    panelID: source?.p?.panelID || panelID,
                    UUID: source?.p?.UUID
                };

                // ✅ restoreCallback: UNDO sonrası local undo moduna geçiş için
                const restoreCallback = (restoreVertices, restoreSource, isRedoContext = false, sceneData = null) => {
 
                    // ✅ Ref'i temizle - handleFirstPoint yeni bellTrafo oluşturacak
                    // BellCommand.undo() bellTrafo'yu siliyor, bu yüzden ref'i null yapıyoruz
                    bellTrafoRef.current = null;

                    dispatch(setCommandType(COMMANDS.DRAW_BELL));

                    setTimeout(() => {
                        const operationData = {
                            initialPoint: restoreVertices[0],
                            source: restoreSource || {
                                type: source?.type,
                                p: source?.p,
                                panelID: source?.p?.panelID,
                                UUID: source?.p?.UUID
                            },
                            _restoreId: Date.now() + Math.random(),
                            // ✅ Bell için ek veriler
                            branchID: sceneData?.branchID,
                            trafoId: sceneData?.trafoId
                        };

                        if (isRedoContext) {
                            // REDO: İlk vertex + geri kalanlar vertexHistory'de
                            operationData.restoreVertices = [restoreVertices[0]];
                            operationData.vertexHistory = restoreVertices.slice(1);
                            operationData.isRedo = true;
                            operationData.onRedoComplete = sceneData?.onRedoComplete;
                         } else {
                            // UNDO: Tüm vertices yükle
                            operationData.restoreVertices = restoreVertices;
                            operationData.isRedo = false;
                            operationData.pendingOutsRemoval = sceneData?.pendingOutsRemoval;
                         }

                        dispatch(setOperationData(operationData));
                    }, 16);
                };

                // ✅ undoRedoManager'ı al (ref'ten veya window'dan)
                const undoRedoManager = undoRedoManagerRef?.current || (typeof window !== 'undefined' ? window.undoRedoManager : null);

                // ✅ REDO modunda yeni transaction OLUŞTURMA - useComponentWiring zaten hallediyor
                // Kullanıcı Ctrl+Y ile tüm vertexleri restore etmeli, Enter ile tamamlanmamalı
                const isRedoModeForTx = operationData?.isRedo === true;
                if (isRedoModeForTx) {
                     // REDO sırasında Enter basıldı - işlemi iptal et, kullanıcı Ctrl+Y ile devam etmeli
                    dispatch(setCommandMessage('REDO: Ctrl+Y ile devam edin veya Escape ile iptal edin'));
                    return { wireId: null, branchId: null };
                }

                if (undoRedoManager) {
                    // 🔵 Transaction başlat
                    const tx = undoRedoManager.beginTransaction('Add Bell Wire');

                    // 🆕 BellCommand oluştur
                    const bellCommand = new BellCommand(
                        'ADD',
                        newBellLoadData,
                        bellFeederData,
                        {
                            trafoId: trafoId,
                            trafoPosition: [trafoPosition.x, trafoPosition.y, trafoPosition.z],
                            direction: [trafoDirection.x, trafoDirection.y, trafoDirection.z],
                            bellId: bellId,
                            bellPosition: [lastVertex.x, lastVertex.y, lastVertex.z],
                            bellAngle: bellAngle,
                            wireId: wireID,
                            branchID: branchID,
                            vertices: vertices.map(v => ({ x: v.x, y: v.y, z: v.z })),
                            wireMarkers: wireMarkersData,
                            color: wireColor,
                            // ✅ Restore için gerekli veriler
                            restoreVertices: vertices.map(v => ({ x: v.x, y: v.y, z: v.z })),
                            restoreSource: restoreSource,
                            restoreCallback: restoreCallback
                        }
                    );

                    // ✅ Command'ı execute et
                    bellCommand.execute(scene, dispatch);

                    // ✅ Transaction'a ekle ve commit et
                    undoRedoManager.addCommand(bellCommand);
                    undoRedoManager.commitTransaction(tx);

                 } else {
                    console.warn('⚠️ [useBell] undoRedoManager bulunamadı - bell eklendi ama undo-redo yok');

                    // Fallback: sadece execute et (undo-redo olmadan)
                    const bellCommand = new BellCommand(
                        'ADD',
                        newBellLoadData,
                        bellFeederData,
                        {
                            trafoId: trafoId,
                            trafoPosition: [trafoPosition.x, trafoPosition.y, trafoPosition.z],
                            direction: [trafoDirection.x, trafoDirection.y, trafoDirection.z],
                            bellId: bellId,
                            bellPosition: [lastVertex.x, lastVertex.y, lastVertex.z],
                            bellAngle: bellAngle,
                            wireId: wireID,
                            branchID: branchID,
                            vertices: vertices.map(v => ({ x: v.x, y: v.y, z: v.z })),
                            wireMarkers: wireMarkersData,
                            color: wireColor
                        }
                    );
                    bellCommand.execute(scene, dispatch);
                }
           

                // ESKİ SİSTEM: Legacy compat için hala userData'ya da ekle
                // ✅ REDO modunda ekleme YAPMA - useComponentWiring zaten ekliyor
                const isRedoMode = operationData?.isRedo === true;
                if (panel.userData && panel.userData.outs && !isRedoMode) {
                    const exitData = {
                        branchID,
                        power,
                        branchType: "bell",
                        description: bellType,
                        length: totalLength.toFixed(2),
                        mcbReference: 213,
                        wires: [{
                            id: wireID,
                            length: totalLength.toFixed(2),
                            bell,
                            power
                        }]
                    };

                    // ✅ addPosition'a göre diziye ekleme yap
                    const addPosition = operationData?.addPosition || 'start';
                    if (addPosition === 'end') {
                        // "Başa ekle" seçili - dizinin BAŞINA ekle
                        panel.userData.outs.unshift(exitData);
                    } else {
                        // "Sona ekle" seçili (default) - dizinin SONUNA ekle
                        panel.userData.outs.push(exitData);
                    }
                }

        dispatch(setCommandMessage('Zil yerleştirildi'));
        dispatch(resetOperation());
        dispatch(clearSelection()); // ✅ Panellerin tıklanabilir olması için selection'ı temizle
        wiringActiveRef.current = false;

        // ✅ Bell trafo referansını temizle
        bellTrafoRef.current = null;

        return { wireId: wireID, branchId: branchID };
    };

    // Item add handler
    const handleItemAdd = (item) => {
    };

    // Use the component wiring hook
    useComponentWiring(
        scene,
        camera,
        renderer,
        [...snapPoints, ...dynamicSnapPoints.current],
        {
            ...bellConfig,
            enabled: () => wiringActiveRef.current,
            onFirstPoint: handleFirstPoint, // ✅ Bell trafo yerleştirme
            onWireEnd: handleWireEnd,
            onSocketPlace: handleBellPlace,
            onItemAdd: handleItemAdd,
            onSocketRemove: handleBellRemove,
            onPreviewUpdate: () => {},
            onWireCancel: () => {
                 // ✅ Bell trafo'yu temizle (scene parametresini kullan, sceneRef yerine)
                if (bellTrafoRef.current) {
                     const targetScene = sceneRef.current || scene;
                    if (targetScene) {
                        targetScene.remove(bellTrafoRef.current);
                        bellTrafoRef.current.traverse(child => {
                            if (child.geometry) child.geometry.dispose();
                            if (child.material) child.material.dispose();
                        });
                     } else {
                        console.warn('   ⚠️ Scene bulunamadı!');
                    }
                    bellTrafoRef.current = null;
                } 
                wiringActiveRef.current = false;
                dispatch(resetOperation());
            }
        }
    );
};

export default useBell;